import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, X, RefreshCw, Sparkles, ImageIcon } from 'lucide-react';
import type { CreateGameKickoffMode } from '../CreateKickoffSelector';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  FEELS_CATALOG,
  ACTIVITIES_CATALOG,
  GENRES_CATALOG,
  MOOD_CATALOG,
  DEFAULT_FEELS,
  DEFAULT_ACTIVITIES,
  DEFAULT_GENRES,
  DEFAULT_MOODS,
  DEFAULT_PACE,
  type CatalogEntry,
} from './catalog';

export interface ConceptStudioProps {
  open: boolean;
  onClose: () => void;
  /** Called when the user commits — the parent composes this into the create
   *  pipeline via `handleCreate(mode, prompt)`. */
  onSubmit: (args: { prompt: string; mode: CreateGameKickoffMode }) => void;
  designDisabledReason?: string;
}

const PITCH_DEBOUNCE_MS = 600;

function paceLabel(pace: number): string {
  if (pace <= 33) return 'Bite-sized';
  if (pace >= 67) return 'Progression-driven';
  return 'Balanced';
}

// ---------------------------------------------------------------------------
// ChipField — chosen items as removable chips + an "＋ Add" popover of
// curated suggestions plus a free write-in. Multi-select. The popover CLOSES
// after each add (or dismiss) — adding several is a deliberate reopen, not the
// default assumption.
// ---------------------------------------------------------------------------

interface ChipFieldProps {
  index: string;
  label: string;
  catalog: CatalogEntry[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Locks the field (used while "Pitch for me" is rolling so a stale roll
   *  response can't clobber edits the user made mid-roll). */
  disabled?: boolean;
}

function ChipField({ index, label, catalog, selected, onChange, disabled }: ChipFieldProps) {
  const [open, setOpen] = useState(false);
  const [writeIn, setWriteIn] = useState('');
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const add = useCallback(
    (value: string) => {
      const v = value.trim();
      if (v && !selected.includes(v)) onChange([...selected, v]);
      setWriteIn('');
      // Close after a single add — don't assume the user wants to keep adding.
      setOpen(false);
    },
    [onChange, selected],
  );

  const remove = useCallback(
    (value: string) => onChange(selected.filter((s) => s !== value)),
    [onChange, selected],
  );

  const iconFor = (value: string) => catalog.find((c) => c.label === value)?.icon;
  const remaining = catalog.filter((c) => !selected.includes(c.label));

  return (
    <div className="mb-6">
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <span className="text-[11px] font-bold" style={{ color: 'var(--text-3)' }}>
          {index}
        </span>
        <h3 className="text-[15px] font-semibold" style={{ color: 'var(--text-1)' }}>
          {label}
        </h3>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {selected.map((value) => {
          const Icon = iconFor(value);
          return (
            <span
              key={value}
              className="inline-flex items-center gap-1.5 rounded-full border border-crucible-accent/40 bg-crucible-accent/10 py-1.5 pl-3 pr-2 text-[13px]"
              style={{ color: 'var(--text-1)' }}
            >
              {Icon && <Icon size={13} className="text-crucible-accent-hover" />}
              {value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                onClick={() => remove(value)}
                disabled={disabled}
                className="flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-white/[0.12] disabled:opacity-50"
                style={{ color: 'var(--text-3)' }}
              >
                <X size={11} strokeWidth={2.2} />
              </button>
            </span>
          );
        })}

        <div ref={popRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-white/20 py-1.5 pl-2.5 pr-3 text-[13px] transition-colors hover:border-white/40 hover:bg-white/[0.05] disabled:opacity-50"
            style={{ color: 'var(--text-2)' }}
          >
            <Plus size={13} strokeWidth={2.2} />
            Add
          </button>

          {open && (
            <div
              className="absolute left-0 top-full z-10 mt-2 w-64 overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
              style={{ background: 'rgb(36, 36, 34)' }}
            >
              <div className="max-h-56 overflow-y-auto py-1">
                {remaining.length === 0 && (
                  <div className="px-4 py-2.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
                    All suggestions added — write your own below.
                  </div>
                )}
                {remaining.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <button
                      key={entry.label}
                      type="button"
                      onClick={() => add(entry.label)}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-white/[0.06]"
                      style={{ color: 'var(--text-1)' }}
                    >
                      <Icon size={15} className="shrink-0 text-crucible-accent-hover" />
                      {entry.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 border-t border-white/[0.08] p-2">
                <input
                  value={writeIn}
                  onChange={(e) => setWriteIn(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      add(writeIn);
                    }
                  }}
                  placeholder="Write your own…"
                  className="min-w-0 flex-1 rounded-lg border border-white/10 bg-transparent px-2.5 py-1.5 text-[13px] outline-none placeholder:text-white/30 focus:border-crucible-accent"
                  style={{ color: 'var(--text-1)' }}
                />
                <button
                  type="button"
                  onClick={() => add(writeIn)}
                  disabled={!writeIn.trim()}
                  aria-label="Add custom"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-crucible-accent/20 text-crucible-accent-hover transition-colors hover:bg-crucible-accent/30 disabled:opacity-40"
                >
                  <Plus size={14} strokeWidth={2.4} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Concept icon — ephemeral generated art, or a deterministic SVG fallback
// (used before any generation and whenever generation is unavailable/fails).
// ---------------------------------------------------------------------------

function IconFallback({ title }: { title: string }) {
  const initial = (title.trim()[0] ?? 'F').toUpperCase();
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      style={{
        background:
          'linear-gradient(150deg, var(--color-crucible-accent, #6366f1) 0%, #0a0322 90%)',
      }}
    >
      <span className="text-5xl font-bold text-white/90">{initial}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConceptStudio
// ---------------------------------------------------------------------------

export function ConceptStudio({
  open,
  onClose,
  onSubmit,
  designDisabledReason,
}: ConceptStudioProps) {
  const [ideas, setIdeas] = useState('');
  const [feels, setFeels] = useState<string[]>(DEFAULT_FEELS);
  const [activities, setActivities] = useState<string[]>(DEFAULT_ACTIVITIES);
  const [genres, setGenres] = useState<string[]>(DEFAULT_GENRES);
  const [moods, setMoods] = useState<string[]>(DEFAULT_MOODS);
  const [pace, setPace] = useState<number>(DEFAULT_PACE);
  const [rolling, setRolling] = useState(false);

  const [title, setTitle] = useState('');
  const [pitch, setPitch] = useState('');
  const [pitchLoading, setPitchLoading] = useState(false);
  // Once the user hand-edits the pitch we stop auto-overwriting it (their
  // "final chance to hand-edit before submission"). Regenerate clears this.
  const [pitchEdited, setPitchEdited] = useState(false);
  // Bumped by "Regenerate" so the effect re-runs even when no selection changed.
  const [regenNonce, setRegenNonce] = useState(0);

  const [iconUrl, setIconUrl] = useState<string | null>(null);
  // The title the current icon was generated for — the image is only shown
  // while it still matches, so a new concept (dice / edits) doesn't display
  // stale art. It reverts to the letter-tile fallback until Refresh art.
  const [iconTitle, setIconTitle] = useState<string | null>(null);
  const [iconLoading, setIconLoading] = useState(false);
  const [iconTried, setIconTried] = useState(false);

  // Monotonic request token so a stale debounced pitch response can't clobber
  // a newer one.
  const pitchReq = useRef(0);
  const iconAutoRef = useRef(false);
  // Signature of the selections the pitch was last generated for — lets us
  // skip a redundant regeneration when nothing relevant changed (e.g. simply
  // reopening the studio), which would otherwise rewrite the pitch and burn a
  // model call.
  const lastKeyRef = useRef<string | null>(null);

  // Signature of the current selections (+ regenNonce so Regenerate always
  // counts as a change). Used both to gate the debounced regeneration AND to
  // tell whether the shown pitch is in sync with the current selections.
  const selectionKey = JSON.stringify({
    ideas,
    feels,
    activities,
    genres,
    moods,
    pace,
    regenNonce,
  });

  // Live pitch — debounced call on any selection change while open, unless
  // the user has taken over the pitch text.
  useEffect(() => {
    if (!open || pitchEdited) return;
    // Skip when nothing that affects the pitch has changed since the last run
    // (e.g. reopening the studio) — avoids a wasted model call and a surprise
    // pitch rewrite.
    if (selectionKey === lastKeyRef.current) return;
    const token = ++pitchReq.current;
    setPitchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await window.crucible.invoke('concept:generate-pitch', {
          selections: { ideas, feels, activities, genres, moods, pace },
        });
        if (token !== pitchReq.current) return; // superseded
        setTitle(res.title);
        setPitch(res.pitch);
        // Record the key only on a COMPLETED generation (not before scheduling),
        // so a run aborted by cleanup — closing the studio mid-debounce / while
        // the IPC is in flight — doesn't mark the key done. Otherwise reopening
        // with the same selections would skip regeneration and strand an empty
        // pitch with the create buttons disabled. (Bugbot #2dbd68fb)
        lastKeyRef.current = selectionKey;
      } catch {
        // Handler unregistered / no key → leave prior text; stop the spinner.
      } finally {
        if (token === pitchReq.current) setPitchLoading(false);
      }
    }, PITCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      // Invalidate any request from this run: if the timer already fired and
      // the IPC is in flight, bumping the token makes its late response fail
      // the `token === pitchReq.current` check so it can't overwrite a pitch
      // the user has since hand-edited (or a newer generation).
      pitchReq.current++;
      // If this run is torn down before its timer fires (the user edited the
      // pitch or closed the studio mid-debounce), the `finally` never runs, so
      // clear the spinner here — otherwise `pitchLoading` strands `true`, which
      // also permanently disables Regenerate. A superseding run re-sets it.
      setPitchLoading(false);
    };
  }, [open, ideas, feels, activities, genres, moods, pace, pitchEdited, regenNonce]);

  const generateIcon = useCallback(async () => {
    if (!title && !pitch) return;
    setIconLoading(true);
    setIconTried(true);
    try {
      const res = await window.crucible.invoke('concept:generate-icon', { title, pitch });
      setIconUrl(res.dataUrl);
      setIconTitle(title);
    } catch {
      // No Fal key / failure → keep the SVG fallback.
      setIconUrl(null);
    } finally {
      setIconLoading(false);
    }
  }, [title, pitch]);

  // Generate art ONCE automatically after the first pitch materialises. All
  // further regenerations are explicit (the "Refresh art" button) — image
  // calls are slow + costly, so we never regenerate per selection change.
  useEffect(() => {
    if (!open || iconAutoRef.current) return;
    if (title || pitch) {
      iconAutoRef.current = true;
      void generateIcon();
    }
  }, [open, title, pitch, generateIcon]);

  const regeneratePitch = useCallback(() => {
    setPitchEdited(false);
    setRegenNonce((n) => n + 1); // force a fresh call even if nothing else changed
  }, []);

  // "Pitch for me" — let the AI pick a coherent set of selections (seeded by
  // whatever's already in the ideas box), then repopulate the fields. The
  // selection change + regenNonce bump regenerates the pitch. Catalog labels
  // are sent as vocab so the model prefers on-catalog (icon-matched) terms.
  const pitchForMe = useCallback(async () => {
    setRolling(true);
    try {
      const res = await window.crucible.invoke('concept:suggest-selections', {
        ideas,
        vocab: {
          feels: FEELS_CATALOG.map((c) => c.label),
          activities: ACTIVITIES_CATALOG.map((c) => c.label),
          genres: GENRES_CATALOG.map((c) => c.label),
          moods: MOOD_CATALOG.map((c) => c.label),
        },
        // Diverge from what's on screen — re-sent each roll so successive
        // rolls chain into wildly different games.
        avoid: { feels, activities, genres, moods, pace },
      });
      setFeels(res.feels);
      setActivities(res.activities);
      setGenres(res.genres);
      setMoods(res.moods);
      setPace(res.pace);
      setPitchEdited(false); // adopt the fresh selections into a new pitch
      setRegenNonce((n) => n + 1);
    } catch {
      // Handler unavailable / no key → leave the current selections untouched.
    } finally {
      setRolling(false);
    }
  }, [ideas, feels, activities, genres, moods, pace]);

  const composePrompt = useCallback((): string => {
    const tail: string[] = [];
    if (ideas.trim()) tail.push(`Starting idea: ${ideas.trim()}`);
    if (feels.length) tail.push(`Feelings: ${feels.join(', ')}`);
    if (activities.length) tail.push(`Players: ${activities.join(', ')}`);
    if (genres.length) tail.push(`Genre mix: ${genres.join(' × ')}`);
    if (moods.length) tail.push(`Moods: ${moods.join(', ')}`);
    tail.push(`Pacing: ${paceLabel(pace)}`);
    const trimmedTitle = title.trim();
    const trimmedPitch = pitch.trim();
    const body = trimmedPitch || trimmedTitle;
    // Prefix the title only when the body is the PITCH — otherwise the body
    // already IS the title (empty pitch) and we'd emit "Title — Title".
    const head = trimmedTitle && trimmedPitch ? `${trimmedTitle} — ` : '';
    return `${head}${body}\n\n(${tail.join(' · ')})`;
  }, [ideas, feels, activities, genres, moods, pace, pitch, title]);

  const submit = useCallback(
    (mode: CreateGameKickoffMode) => {
      onSubmit({ prompt: composePrompt(), mode });
    },
    [onSubmit, composePrompt],
  );

  // Submittable only when the shown pitch is IN SYNC with the current
  // selections — either the user hand-edited it, or it was generated for
  // exactly these selections (`lastKeyRef === selectionKey`). Gating on this,
  // not just pitchLoading/rolling, closes the frame after a roll / Regenerate /
  // chip change where CTAs would otherwise be enabled while title/pitch still
  // reflect the prior selections but composePrompt already uses the new tail
  // → a create with a mismatched prompt. (Bugbot #74dc36b0)
  const armed = Boolean(pitch.trim() || title.trim());
  const pitchInSync = pitchEdited || lastKeyRef.current === selectionKey;
  const canSubmit = armed && !pitchLoading && !rolling && pitchInSync;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showClose
        className="w-[min(96vw,1040px)] max-w-none p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Concept Studio</DialogTitle>
        <div className="grid max-h-[85vh] grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          {/* ---------- LEFT: choices ---------- */}
          <div className="overflow-y-auto border-b border-white/[0.06] p-6 md:border-b-0 md:border-r">
            <div className="mb-6">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.16em] text-crucible-accent-hover">
                Concept Studio
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-[22px] font-bold" style={{ color: 'var(--text-1)' }}>
                    Shape your idea.
                  </h2>
                  <p className="mt-1 text-[13px]" style={{ color: 'var(--text-3)' }}>
                    Every choice reshapes the game on the right. All Foundry games play on a TV with
                    phones as controllers.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void pitchForMe()}
                  disabled={rolling}
                  data-testid="concept-studio-pitch-for-me"
                  title="Let the AI pick a good combination"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-[13px] font-medium transition-colors hover:bg-white/[0.06] disabled:opacity-50"
                  style={{ color: 'var(--text-1)' }}
                >
                  {rolling ? (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/25 border-t-white/70" />
                  ) : (
                    <span aria-hidden className="text-[15px] leading-none">
                      🎲
                    </span>
                  )}
                  {rolling ? 'Rolling…' : 'Pitch for me'}
                </button>
              </div>
            </div>

            {/* Free-text starting idea */}
            <div className="mb-6">
              <div className="mb-2.5 flex items-baseline gap-2.5">
                <h3 className="text-[15px] font-semibold" style={{ color: 'var(--text-1)' }}>
                  Any ideas you have already?
                </h3>
                <span className="text-[12px]" style={{ color: 'var(--text-3)' }}>
                  optional
                </span>
              </div>
              <textarea
                value={ideas}
                onChange={(e) => setIdeas(e.target.value)}
                disabled={rolling}
                placeholder="Anything you've got — a theme, a twist, a reference. Leave blank to just explore."
                rows={2}
                className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[14px] leading-relaxed outline-none placeholder:text-white/30 focus:border-crucible-accent disabled:opacity-50"
                style={{ color: 'var(--text-1)' }}
              />
            </div>

            <ChipField
              index="01"
              label="What do you want players to feel?"
              catalog={FEELS_CATALOG}
              selected={feels}
              onChange={setFeels}
              disabled={rolling}
            />
            <ChipField
              index="02"
              label="What will players do?"
              catalog={ACTIVITIES_CATALOG}
              selected={activities}
              onChange={setActivities}
              disabled={rolling}
            />
            <ChipField
              index="03"
              label="Genres to mix"
              catalog={GENRES_CATALOG}
              selected={genres}
              onChange={setGenres}
              disabled={rolling}
            />
            <ChipField
              index="04"
              label="Set the mood."
              catalog={MOOD_CATALOG}
              selected={moods}
              onChange={setMoods}
              disabled={rolling}
            />

            {/* Pace */}
            <div className="mb-2">
              <div className="mb-3 flex items-baseline gap-2.5">
                <span className="text-[11px] font-bold" style={{ color: 'var(--text-3)' }}>
                  05
                </span>
                <h3 className="text-[15px] font-semibold" style={{ color: 'var(--text-1)' }}>
                  How does it play?
                </h3>
                <span className="ml-auto text-[13px] font-semibold text-crucible-accent-hover">
                  {paceLabel(pace)}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={pace}
                onChange={(e) => setPace(Number(e.target.value))}
                disabled={rolling}
                aria-label="Pacing — bite-sized to progression-driven"
                className="w-full accent-crucible-accent disabled:opacity-50"
              />
              <div
                className="mt-2 flex justify-between text-[12px]"
                style={{ color: 'var(--text-3)' }}
              >
                <span>Bite-sized</span>
                <span>Progression-driven</span>
              </div>
            </div>
          </div>

          {/* ---------- RIGHT: the vision ---------- */}
          <div className="flex flex-col overflow-y-auto p-6">
            <div className="flex items-start gap-4">
              <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-white/10">
                {iconUrl && iconTitle === title ? (
                  <img src={iconUrl} alt="Concept icon" className="h-full w-full object-cover" />
                ) : (
                  <IconFallback title={title} />
                )}
                {iconLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white/80" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em]"
                  style={{ color: 'var(--text-3)' }}
                >
                  Working title
                </div>
                <h2
                  className="break-words text-[26px] font-bold leading-tight"
                  style={{ color: 'var(--text-1)' }}
                >
                  {title || (pitchLoading ? 'Dreaming…' : 'Your game')}
                </h2>
                <button
                  type="button"
                  onClick={() => void generateIcon()}
                  disabled={iconLoading || !armed}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[12px] transition-colors hover:bg-white/[0.06] disabled:opacity-40"
                  style={{ color: 'var(--text-2)' }}
                >
                  <ImageIcon size={12} />
                  {iconTried ? 'Refresh art' : 'Generate art'}
                </button>
              </div>
            </div>

            {/* Pitch */}
            <div className="mt-5 flex items-center justify-between">
              <div
                className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em]"
                style={{ color: 'var(--text-3)' }}
              >
                <Sparkles size={12} className="text-crucible-accent-hover" />
                The pitch
                {pitchLoading && (
                  <span className="ml-1 h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-white/60" />
                )}
              </div>
              <button
                type="button"
                onClick={regeneratePitch}
                disabled={pitchLoading || rolling}
                className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12px] transition-colors hover:bg-white/[0.06] disabled:opacity-40"
                style={{ color: 'var(--text-3)' }}
                title="Regenerate from your choices"
              >
                <RefreshCw size={12} />
                Regenerate
              </button>
            </div>
            <textarea
              value={pitch}
              onChange={(e) => {
                setPitch(e.target.value);
                setPitchEdited(true);
              }}
              // Locked while a dice roll is in flight — like the chips/ideas/pace
              // — so an edit made mid-roll isn't discarded when the roll finishes
              // and regenerates the pitch (clears pitchEdited + bumps regenNonce).
              disabled={rolling}
              placeholder="Your pitch will appear here as you make choices…"
              rows={5}
              className="mt-2 w-full flex-1 resize-none rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-[15px] leading-relaxed outline-none focus:border-crucible-accent disabled:opacity-50"
              style={{ color: 'var(--text-1)' }}
            />
            <p className="mt-1.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
              Edit anything above — this is exactly what gets built.
            </p>

            {/* CTA row */}
            <div className="mt-4 flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={() => submit('quick')}
                disabled={!canSubmit}
                className="flex-1 rounded-2xl bg-crucible-accent px-4 py-3.5 text-[15px] font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-40"
              >
                Start guided build
              </button>
              <button
                type="button"
                onClick={() => submit('advanced')}
                disabled={!canSubmit || designDisabledReason !== undefined}
                className="flex-1 rounded-2xl border border-white/15 px-4 py-3.5 text-[15px] font-semibold transition-colors hover:bg-white/[0.06] disabled:opacity-40"
                style={{ color: 'var(--text-1)' }}
                title={designDisabledReason}
              >
                Open Design
              </button>
            </div>
            <p className="mt-2 text-center text-[12px]" style={{ color: 'var(--text-3)' }}>
              Guided build asks a short interview. Design lets you author the plan before building.
              {designDisabledReason ? ` ${designDisabledReason}` : ''}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
